'use client';

import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { clsx } from 'clsx';
import {
  Children,
  cloneElement,
  isValidElement,
  type ComponentPropsWithRef,
  type ReactNode,
} from 'react';

import { Loader } from '@repo/ui/Loader';

import s from './Button.module.css';

export const buttonVariantOptions = [
  'plain',
  'primary',
  'secondary',
  'tertiary',
] as const;
export type ButtonVariant = (typeof buttonVariantOptions)[number];

export const buttonStatusOptions = ['neutral', 'destructive'] as const;
export type ButtonStatus = (typeof buttonStatusOptions)[number];

export const buttonShapeOptions = ['default', 'icon', 'micro'] as const;
export type ButtonShape = (typeof buttonShapeOptions)[number];

export const buttonSizeOptions = ['xs', 'sm', 'md', 'lg'] as const;
export type ButtonSize = (typeof buttonSizeOptions)[number];

// Size constraints per shape category
export type DefaultIconSize = Exclude<ButtonSize, 'xs'>;
export type MicroSize = Exclude<ButtonSize, 'md' | 'lg'>;

// Variant constraints per shape category
export type DefaultShapeVariant = Exclude<ButtonVariant, 'tertiary'>;
export type IconShapeVariant = Exclude<ButtonVariant, 'plain'>;
export type MicroShapeVariant = Exclude<ButtonVariant, 'plain' | 'primary'>;

// Shape-size-variant discriminated unions
export type DefaultShapeProps = {
  shape?: 'default';
  size?: DefaultIconSize;
  isFullWidth?: boolean;
  variant?: DefaultShapeVariant;
};

type IconShapeProps = {
  shape: 'icon';
  size?: DefaultIconSize;
  variant?: IconShapeVariant;
};

type MicroShapeProps = {
  shape: 'micro';
  size?: MicroSize;
  variant?: MicroShapeVariant;
};

type ShapeSizeProps = DefaultShapeProps | IconShapeProps | MicroShapeProps;

// Common props shared by all button variants (excluding shape/size/variant which come from ShapeSizeProps)
export interface ButtonCommonProps extends ComponentPropsWithRef<'button'> {
  ariaDisabled?: boolean;
  className?: string;
  disabled?: boolean;
  status?: ButtonStatus;
  isLoading?: boolean;
  loadingLabel?: string;
}

export type ButtonBaseProps = ButtonCommonProps & ShapeSizeProps;

export type ButtonProps = ButtonCommonProps &
  ShapeSizeProps & {
    asChild?: boolean;
    children: ReactNode;
  };

export const buttonVariants = cva(s.button, {
  variants: {
    status: {
      neutral: s.neutralStatus,
      destructive: s.destructiveStatus,
    },
    shape: {
      default: s.defaultShape,
      icon: s.iconShape,
      micro: s.microShape,
    },
    variant: {
      plain: s.plainVariant,
      primary: s.primaryVariant,
      secondary: s.secondaryVariant,
      tertiary: s.tertiaryVariant,
    },
    size: {
      xs: s.xsSize,
      sm: s.smSize,
      md: s.mdSize,
      lg: s.lgSize,
    },
    isFullWidth: {
      true: s.isFullWidth,
    },
    isLoading: {
      true: s.isLoading,
    },
  },
});

export const Button = (props: ButtonProps) => {
  const {
    ariaDisabled,
    asChild = false,
    children,
    className,
    disabled = false,
    status = 'neutral',
    isLoading = false,
    loadingLabel = 'Loading',
    onClick,
    shape = 'default',
    size = 'md',
    type = 'button',
    ...rest
  } = props;

  // Extract shape-specific props to avoid spreading to DOM
  const {
    isFullWidth: isFullWidthProp,
    variant: variantProp,
    ...attrs
  } = rest as {
    isFullWidth?: boolean;
    variant?: ButtonVariant;
  } & typeof rest;

  const isFullWidth = isFullWidthProp ?? false;
  // Default variant depends on shape: micro defaults to 'secondary', others to 'primary'
  const variant = variantProp ?? (shape === 'micro' ? 'secondary' : 'primary');

  if (!children) {
    return null;
  }

  const handleClick: ComponentPropsWithRef<'button'>['onClick'] = (e) => {
    if (onClick && !disabled && !ariaDisabled && !isLoading) {
      onClick(e);
    }
  };

  const Component = asChild ? Slot : 'button';
  const htmlButtonType = Component === 'button' ? type : undefined;
  const appearsDisabled = ariaDisabled || disabled ? true : undefined;

  const loaderSize = shape === 'micro' ? size : 'xs';
  const loader = isLoading && (
    <Loader
      key="loader"
      label={loadingLabel}
      size={loaderSize}
      className={s.loader}
    />
  );

  let content;

  if (asChild) {
    const child = Children.only(children);
    if (!isValidElement<{ children?: ReactNode }>(child)) {
      throw new Error('Button child must be a valid React element');
    }
    const childElement = child;
    // Clone the child but add the span inside it and the loader after
    content = cloneElement(
      childElement,
      attrs,
      <>
        <span className={s.children}>{childElement.props.children}</span>
        {loader}
      </>,
    );
  } else {
    content = (
      <>
        <span className={s.children}>{children}</span>
        {loader}
      </>
    );
  }

  return (
    <Component
      aria-busy={isLoading}
      aria-disabled={appearsDisabled}
      className={clsx(
        buttonVariants({
          status,
          shape,
          size,
          variant,
          isFullWidth,
          isLoading,
          className,
        }),
      )}
      data-button-shape={shape}
      disabled={disabled}
      onClick={handleClick}
      type={htmlButtonType}
      {...attrs}
    >
      {content}
    </Component>
  );
};
